import { createSelector } from 'reselect';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import { selectedProductSkus } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import {
    selectPreferencesMaxOrderQuantity,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { getDisableBYDeliveryDateFlag } from 'yoda-site-components/lib/selectors/byDeliveryDateSelector';
import DateFormatter from 'yoda-site-components/lib/dates/DateFormatter/DateFormatter';
import { promiseBopisNodes } from 'yoda-site-components/lib/common/ProductConstants';
import getNextBusinessDay from 'yoda-interfaces/lib/Product/getNextBusinessDay';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import ProductConstants from '../../helpers/ProductConstants';
import Capitalize from '../../helpers/Capitalize';
import FreePickupLabel from '../../components/DeliveryOption/FreePickupLabel';

const {
    NOT_AVAILABLE_SHORT,
    SAMEDAYPICKUP_FLAG,
    DISABLED,
    PICKUP_UNAVAILABLE_SELECTED_STORE,
    PICKUP_UNAVAILABLE_NEARBY_STORE,
} = ProductConstants;

export const isStoreAvailable = ({ productOffering, context }) => {
    const skuAvailableInNearbyStore = get(
        productOffering,
        `promise.skuDeliveryOptions[0].deliveryOptions.${getBopusStoreNode()}.locationIds[0].skuAvailableInNearbyStore`,
        false
    );
    if (skuAvailableInNearbyStore) {
        return NOT_AVAILABLE_SHORT;
    }
    return get(
        productOffering,
        `promise.skuDeliveryOptions[0].deliveryOptions.${getBopusStoreNode()}.locationIds[0].availabilityStatus`,
        NOT_AVAILABLE_SHORT
    );
};

export const isSameDayPickupQuantityAllowed = ({ productOffering, selectedQuantity, context }) =>
    selectedQuantity <=
    get(
        productOffering,
        `promise.skuDeliveryOptions[0].deliveryOptions.${getBopusStoreNode()}.locationIds[0].availableQuantity`,
        0
    );

const getBopusStoreNode = () => promiseBopisNodes.aggregatedBopusNode;

/* istanbul ignore next */
/* eslint operator-assignment: ['error', 'never'] */
const dateCalculator = (date) => {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    const strTime = `${hours}:${minutes}${ampm}`;
    const { formattedDate } = formatDateAsMMDDYYYY(date);
    return `inventory updated ${strTime}  ${formattedDate}`;
};

const formatDateAsMMDDYYYY = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    const monthForAnalytics = `0${date.getMonth() + 1}`.slice(-2);
    const dayForAnalytics = `0${date.getDate()}`.slice(-2);

    return {
        formattedDate: `${month}/${day}/${year}`,
        formattedDateForAnalytics: `${monthForAnalytics}/${dayForAnalytics}/${year}`,
    };
};

/**
 * [setShipToHomeOrStore description]
 * @param {[type]} productDefaultLot [description]
 */
const setShipToHomeOrStore = (
    store,
    productDefaultLot,
    storeDetails,
    configMaxOrderQuantity,
    selectedQuantity,
    maxOrderQuantityEnabled,
    selectedDeliveryOption,
    availableStore,
    allowedSamedayPickupQuantity,
    featureFlag,
    byDeliveryDateFlag,
    isMarketplaceProduct
) => {
    let shipToHomeOrStore;
    const { channelOffering, canShipToStore, shipping } = productDefaultLot || {};
    const { inventory, pricing, promise } = store || {};
    const availabilityStatus = ['AH', 'AL', 'BO'];
    /* istanbul ignore next */
    const promiseDetails = get(promise, 'skuDeliveryOptions[0].deliveryOptions', false);
    const sameDayPickup = get(promiseDetails, getBopusStoreNode(), '');
    const shipToHomeAvailability = get(promiseDetails, 'shipToHome', false);
    const { SHIPTOSTORE, SHIPTOHOME, NOTAVAILABLE } = ProductConstants;
    // For marketplace products, always use SHIPTOHOME label
    const label = isMarketplaceProduct ? SHIPTOHOME : canShipToStore ? SHIPTOSTORE : SHIPTOHOME;
    let isQuantityAvailable = true;
    let deliveryDateForAnalytics = '';
    /* istanbul ignore else */
    if (
        (channelOffering && channelOffering.toLowerCase() === 'onlineonly') ||
        (shipToHomeAvailability &&
            availabilityStatus.indexOf(shipToHomeAvailability.availabilityStatus) !== -1) ||
        sameDayPickup ||
        !Object.keys(storeDetails).length
    ) {
        let isshipToStoreDisabled = false;
        let isRadioButtonDisabled = false;
        let disabled = '';
        let isChecked =
            get(selectedDeliveryOption, 'selectedOption', false) !== SAMEDAYPICKUP_FLAG ||
            availableStore === NOT_AVAILABLE_SHORT ||
            !allowedSamedayPickupQuantity;
        let maxQuantity = '';
        let maxQuantityMsgDisplay = false;
        let quantityReachMaxPosition = false;
        const updatedDate = inventory && inventory.updatedAsOf ? inventory.updatedAsOf : false;
        const serviceLevels = shipToHomeAvailability ? shipToHomeAvailability.serviceLevels : false;
        let deliveryDateStart = '';
        let deliveryDateEnd = '';
        let deliveryMessage = '';
        let rawDeliveryDateStart = '';
        let expDeliveryDate = '';
        let difference_In_Days = '';
        if (serviceLevels && serviceLevels[0]) {
            const hasDateRange =
                serviceLevels[0].deliveryDateStart && serviceLevels[0].deliveryDateEnd;
            const dateFormat =
                (!byDeliveryDateFlag && serviceLevels[0].deliveryDate) ||
                (isMarketplaceProduct && hasDateRange)
                    ? 'Wd, MMM Do'
                    : 'MMM Do';
            const { formattedDateForAnalytics } =
                (!byDeliveryDateFlag && serviceLevels[0].deliveryDate) || hasDateRange
                    ? formatDateAsMMDDYYYY(
                          new Date(
                              serviceLevels[0].deliveryDate || serviceLevels[0].deliveryDateStart
                          )
                      )
                    : formatDateAsMMDDYYYY(new Date(serviceLevels[0].deliveryDateEnd)) || '';
            deliveryDateForAnalytics = formattedDateForAnalytics;
            deliveryDateStart = DateFormatter.format(
                dateFormat,
                hasDateRange
                    ? serviceLevels[0].deliveryDateStart
                    : serviceLevels[0].deliveryDate || serviceLevels[0].deliveryDateStart,
                store.isFallBack
            );
            deliveryDateEnd = DateFormatter.format(
                dateFormat,
                hasDateRange
                    ? serviceLevels[0].deliveryDateEnd
                    : serviceLevels[0].deliveryDate || serviceLevels[0].deliveryDateEnd,
                store.isFallBack
            );

            /* istanbul ignore next */
            if (shipping && shipping.truckable) {
                const shipDate =
                    DateFormatter.format('MMM Do', shipToHomeAvailability.shipDate, true) || '';
                const shipDateFormatted =
                    byDeliveryDateFlag || hasDateRange
                        ? shipDate.toLowerCase()
                        : Capitalize.toCapitalize(shipDate);
                deliveryMessage = `We will contact you by ${shipDateFormatted}
                 to schedule a delivery time.${
                     shipping.shipFromFactory
                         ? ' This item ships directly from the manufacturer.'
                         : ''
                 }`;
                rawDeliveryDateStart = shipToHomeAvailability.shipDate;
            } else {
                const deliveryMessagePrefix =
                    (!byDeliveryDateFlag && serviceLevels[0].deliveryDate) ||
                    (isMarketplaceProduct && hasDateRange)
                        ? 'Get it'
                        : 'Get it by';
                deliveryMessage = `${deliveryMessagePrefix} ${uniq([
                    deliveryDateStart,
                    deliveryDateEnd,
                ]).join(' - ')}`;
                rawDeliveryDateStart =
                    serviceLevels[0].deliveryDate || serviceLevels[0].deliveryDateEnd;
            }
            const dateOptions = {
                timeZone: 'America/Mexico_City',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };
            const formatter = new Intl.DateTimeFormat('en-US', dateOptions);
            const formatDateInUSTimeZone = (date) => {
                const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
                return date.toLocaleDateString('en-US', options).replace(/\//g, '/');
            };
            const formattedDate = formatter.format(new Date());
            const [month, day, year] = formattedDate.split('/');
            const formattedCurrentDate = `${month}/${day}/${year}`;

            const deliveryDate = serviceLevels[0]?.deliveryDate
                ? serviceLevels[0].deliveryDate.toString()
                : serviceLevels[0].deliveryDateEnd?.toString() || '';
            if (deliveryDate) {
                const removeTime = new Date(deliveryDate.replace(/\s\d{2}:\d{2}:\d{2}/, ''));
                const dateFormattted = new Date(deliveryDate);
                const [
                    { value: month },
                    ,
                    { value: day },
                    ,
                    { value: year },
                ] = formatter.formatToParts(dateFormattted);
                const iOSFormat = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
                const isValidDateFormat = (input) => {
                    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
                    return regex.test(input);
                };

                expDeliveryDate = isValidDateFormat(formatDateInUSTimeZone(removeTime))
                    ? formatDateInUSTimeZone(removeTime) || ''
                    : isValidDateFormat(iOSFormat) && iOSFormat;
                const date1 = new Date(formattedCurrentDate);
                const date2 = new Date(expDeliveryDate);
                const Difference_In_Time = Math.abs(date2.getTime() - date1.getTime());
                difference_In_Days = Math.floor(Difference_In_Time / (1000 * 60 * 60 * 24));
            }
        } else if (
            shipToHomeAvailability &&
            shipToHomeAvailability.availabilityStatus &&
            shipToHomeAvailability.availabilityStatus === 'AH'
        ) {
            deliveryDateStart = DateFormatter.format('MMM Do', new Date(), true);
            deliveryDateEnd = DateFormatter.format('MMM Do', getNextBusinessDay(7), true);
            deliveryMessage = `Get it by ${deliveryDateStart} - ${deliveryDateEnd}`;
        }
        let surcharges = pricing && pricing.surcharges ? `$${pricing.surcharges[0].message}` : '';
        let availabilityMessage = '';
        let updatedAsOf = '';
        if (
            !shipToHomeAvailability ||
            shipToHomeAvailability.availabilityStatus === NOT_AVAILABLE_SHORT
        ) {
            isshipToStoreDisabled = true;
            isRadioButtonDisabled = true;
            disabled = DISABLED;
            isChecked = false;
            deliveryMessage = '';
            availabilityMessage = NOTAVAILABLE;
            surcharges = '';
        }
        if (maxOrderQuantityEnabled) {
            /* istanbul ignore next */
            if (shipToHomeAvailability && shipToHomeAvailability.availableQuantity) {
                if (configMaxOrderQuantity > shipToHomeAvailability.availableQuantity) {
                    maxQuantity = `${shipToHomeAvailability.availableQuantity} left!`;
                    maxQuantityMsgDisplay = true;
                    updatedAsOf = updatedDate ? dateCalculator(new Date(updatedDate)) : '';
                }
                /* istanbul ignore next */
                if (selectedQuantity === shipToHomeAvailability.availableQuantity) {
                    quantityReachMaxPosition = true;
                }
                /* istanbul ignore next */
                if (selectedQuantity > shipToHomeAvailability.availableQuantity) {
                    isshipToStoreDisabled = true;
                    isRadioButtonDisabled = true;
                    disabled = DISABLED;
                    isChecked = false;
                    deliveryMessage = '';
                    surcharges = '';
                    isQuantityAvailable = false;
                }
            }
        }

        shipToHomeOrStore = {
            canShipToStore: isMarketplaceProduct ? false : canShipToStore,
            label,
            deliveryMessage,
            availabilityMessage,
            isshipToStoreDisabled,
            isRadioButtonDisabled,
            disabled,
            isChecked,
            surcharges,
            isShipToHomeOrStoreAvailable: true,
            maxQuantity,
            isQuantityAvailable,
            maxQuantityMsgDisplay,
            quantityReachMaxPosition,
            updatedAsOf,
            rawDeliveryDateStart,
            deliveryDateForAnalytics,
            shipToHomeAvailability,
            expDeliveryDate,
            difference_In_Days,
        };
    } else if (!shipToHomeAvailability) {
        shipToHomeOrStore = {
            canShipToStore,
            label,
            isshipToStoreDisabled: true,
            isRadioButtonDisabled: true,
            isShipToHomeOrStoreAvailable: true,
            disabled: DISABLED,
            isChecked: false,
            deliveryMessage: '',
            availabilityMessage: NOTAVAILABLE,
            surcharges: '',
            deliveryDateForAnalytics,
        };
    } else {
        shipToHomeOrStore = {
            isShipToHomeOrStoreAvailable: false,
        };
    }
    return shipToHomeOrStore;
};

/**
 * [calculateSameDayPickup description]
 * @param  {[type]} sameDayPickup [description]
 * @param  {[type]} inventory     [description]
 * @return {[type]}               [description]
 */
const calculateSameDayPickup = (
    sameDayPickup,
    updatedDate,
    storeDetails,
    selectedQuantity,
    selectedDeliveryOption,
    configMaxOrderQuantity,
    maxOrderQuantityEnabled,
    shipToHomeAvailability,
    selectedStoreDetails,
    availableStore,
    featureFlag
) => {
    const { NOTAVAILABLE } = ProductConstants;
    const { availabilityStatus = NOT_AVAILABLE_SHORT, availableQuantity, message } = sameDayPickup;
    const label = FreePickupLabel;
    let updatedAsOf = '';
    let quantities = '';
    let storeInfo = '';

    if (selectedStoreDetails && selectedStoreDetails.storeName) {
        storeInfo = selectedStoreDetails && selectedStoreDetails.storeName;
    }
    const unAvailableArray = ['NOTAVAILABLE', 'OUTOFSTOCK'];
    const isRadioButtonDisabled = false;
    let isChecked =
        (get(selectedDeliveryOption, 'selectedOption') === SAMEDAYPICKUP_FLAG ||
            get(shipToHomeAvailability, 'availabilityStatus', NOT_AVAILABLE_SHORT) ===
                NOT_AVAILABLE_SHORT) &&
        availableStore !== NOT_AVAILABLE_SHORT;
    let availibilityClass = unAvailableArray.indexOf(message) !== -1 ? 'outOfStock' : '';
    let availabilityMessage = '';
    let staticMessage = 'Order before 3 pm to get same day pickup';
    let isSameDayPickupDisabled = false;
    let disabled = '';
    let skuAvailableInNearbyStore = false;
    const storeMessage = 'Change';
    /* istanbul ignore next */
    if (
        availabilityStatus === 'AL' ||
        (maxOrderQuantityEnabled && availableQuantity && configMaxOrderQuantity > availableQuantity)
    ) {
        quantities = `Hurry, ${availableQuantity} left!`;
        updatedAsOf = updatedDate ? dateCalculator(new Date(updatedDate)) : '';
    }
    /* istanbul ignore next */
    if (availabilityStatus === NOT_AVAILABLE_SHORT) {
        isSameDayPickupDisabled = true;
        disabled = 'disabled';
        staticMessage = '';
        availabilityMessage = NOTAVAILABLE;
        availibilityClass = 'outOfStock';
    } else if (availableQuantity && selectedQuantity > availableQuantity) {
        quantities = `Hurry, ${availableQuantity} left!`;
        availibilityClass = 'outOfStock';
        availabilityMessage = NOTAVAILABLE;
        isSameDayPickupDisabled = true;
        disabled = DISABLED;
        staticMessage = '';
        updatedAsOf = updatedDate ? dateCalculator(new Date(updatedDate)) : '';
        isChecked = false;
    }
    if (sameDayPickup.skuAvailableInNearbyStore) {
        isChecked = false;
        skuAvailableInNearbyStore = true;
        availabilityMessage = NOTAVAILABLE;
        availibilityClass = 'outOfStock';
        quantities = '';
    }
    return {
        label,
        availabilityMessage,
        quantities,
        isChecked,
        disabled,
        staticMessage,
        isSameDayPickupDisabled,
        isRadioButtonDisabled,
        storeInfo,
        availibilityClass,
        storeMessage,
        updatedAsOf,
        skuAvailableInNearbyStore,
    };
};

/**
 * [setSameDayPickUp description]
 * @param {[type]} productDefaultLot [description]
 */
const setSameDayPickUp = (
    productDetails,
    store,
    productDefaultLot,
    storeDetails,
    selectedQuantity,
    selectedDeliveryOption,
    configMaxOrderQuantity,
    maxOrderQuantityEnabled,
    selectedStoreDetails,
    availableStore,
    featureFlag,
    byDeliveryDateFlag,
    isMarketplaceProduct = false
) => {
    const { SAMEDAYPICKUP, NOTAVAILABLE, OUTOFSTOCK, STORE_PICKUP_LABEL } = ProductConstants;
    const { channelOffering } = productDefaultLot || {};
    const { promise } = store || {};
    const promiseDetails = get(promise, 'skuDeliveryOptions[0].deliveryOptions', false);
    const stores = get(promiseDetails, getBopusStoreNode(), '');
    const shipToHomeAvailability = get(promiseDetails, 'shipToHome', false);
    const isSameDayPickupDisabled = true;
    const isRadioButtonDisabled = false;
    let label = SAMEDAYPICKUP;
    const disabled = DISABLED;
    const updatedAsOf = '';
    let availabilityMessage = NOTAVAILABLE;
    const storeAvailabilityMessage = OUTOFSTOCK;
    let isChecked = false;
    let storeMessage = '';
    let channelOfferingLabel = '';
    let calcSameDayPickup = '';
    let isVisitStoreToPurchase = false;
    let storeInfoVisitToPurchase = '';

    /* istanbul ignore next */
    if (storeDetails && storeDetails.name) {
        storeInfoVisitToPurchase = storeDetails.name;
    } else if (selectedStoreDetails && selectedStoreDetails.name) {
        storeInfoVisitToPurchase = selectedStoreDetails.name;
    }
    const sameDayPickupEligibility = productDetails.bopisEligibility && !isMarketplaceProduct;
    /* istanbul ignore next */
    if (
        (!channelOffering ||
            (channelOffering && channelOffering.toLowerCase() === 'omnichannel')) &&
        !isMarketplaceProduct
    ) {
        if (!stores && !sameDayPickupEligibility) {
            isVisitStoreToPurchase = true;
        }
        if (!stores && storeInfoVisitToPurchase === '') {
            label = FreePickupLabel;
            storeMessage = STORE_PICKUP_LABEL;
            availabilityMessage = '';
        } else {
            const storeInventory = stores && stores.locationIds ? stores.locationIds[0] : false;
            if (sameDayPickupEligibility) {
                calcSameDayPickup = calculateSameDayPickup(
                    storeInventory,
                    '',
                    storeDetails,
                    selectedQuantity,
                    selectedDeliveryOption,
                    configMaxOrderQuantity,
                    maxOrderQuantityEnabled,
                    shipToHomeAvailability,
                    selectedStoreDetails,
                    availableStore,
                    featureFlag
                );
            } else {
                isVisitStoreToPurchase = true;
            }
        }
        if (!get(featureFlag, 'bopis', true) || isMarketplaceProduct) {
            isVisitStoreToPurchase = true;
        }
    } else {
        channelOfferingLabel =
            channelOffering && channelOffering.toLowerCase() === 'onlineonly'
                ? 'Online only'
                : 'In Store Only';
        storeMessage = '';
        isChecked = false;
        label = '';
    }
    let pickupDateMessage = get(
        promiseDetails,
        `${promiseBopisNodes.bopusNode}.locationIds[0].pickupDateMessage`,
        ''
    );
    if (
        calcSameDayPickup.availabilityMessage === NOTAVAILABLE &&
        (byDeliveryDateFlag || isEmpty(pickupDateMessage))
    ) {
        pickupDateMessage = PICKUP_UNAVAILABLE_SELECTED_STORE;
        if (!calcSameDayPickup.skuAvailableInNearbyStore) {
            pickupDateMessage = PICKUP_UNAVAILABLE_NEARBY_STORE;
        }
    }
    const sameDayPickup = {
        label,
        availabilityMessage,
        storeAvailabilityMessage,
        isSameDayPickupDisabled,
        isRadioButtonDisabled,
        storeMessage,
        disabled,
        isChecked,
        channelOfferingLabel,
        isVisitStoreToPurchase,
        storeInfoVisitToPurchase,
        updatedAsOf,
        pickupDateMessage,
        ...calcSameDayPickup,
    };
    return sameDayPickup;
};

const getProductDetails = (state) => state.productDetails;
const getProductOffering = (state) => state.productOffering;
const getQuantity = (state) => state.selectedQuantity;
const getDeliveryOptionsName = (state) => state.deliveryOptionsName;
const getSelectedStore = (state) => state.selectedStore;
const getSdpuStoreInfo = (state) => state.sdpuStoreInfo;

const initDeliveryOptions = createSelector(
    [
        getProductDetails,
        getProductOffering,
        getQuantity,
        getDeliveryOptionsName,
        selectedProductSkus(false),
        selectPreferencesMaxOrderQuantity,
        selectFeatureFlags,
        getSelectedStore,
        isStoreAvailable,
        isSameDayPickupQuantityAllowed,
        getSdpuStoreInfo,
        getDisableBYDeliveryDateFlag,
        selectMarketPlaceSellerInfo(false),
    ],
    (
        productDetails,
        productOffering,
        quantity,
        deliveryOptionsName,
        productSKUs,
        maxOrderQuantity,
        featureFlag,
        selectedStore,
        availableStore,
        allowedSamedayPickupQuantity,
        sdpuStoreInfo,
        byDeliveryDateFlag,
        marketplaceInfo
    ) => {
        if (productOffering && Object.keys(productOffering).length > 0) {
            const selectedStoreDetails =
                selectedStore && selectedStore.storeDetails ? selectedStore.storeDetails : '';
            const storeDetails = selectedStoreDetails;
            const { maxOrderQuantityEnabled = true, enableMarketPlace = false } = featureFlag;
            const isMarketplaceProduct =
                enableMarketPlace && (marketplaceInfo?.isMarketPlaceProduct || false);
            const sameDayPickUp = setSameDayPickUp(
                productDetails,
                productOffering,
                productSKUs[0],
                storeDetails,
                quantity,
                deliveryOptionsName,
                maxOrderQuantity,
                maxOrderQuantityEnabled,
                selectedStoreDetails,
                availableStore,
                featureFlag,
                byDeliveryDateFlag,
                isMarketplaceProduct
            );
            const shipToHomeOrStore = setShipToHomeOrStore(
                productOffering,
                productSKUs[0],
                storeDetails,
                maxOrderQuantity,
                quantity,
                maxOrderQuantityEnabled,
                deliveryOptionsName,
                availableStore,
                allowedSamedayPickupQuantity,
                featureFlag,
                byDeliveryDateFlag,
                isMarketplaceProduct
            );
            return { shipToHomeOrStore, sameDayPickUp };
        }
        return { shipToHomeOrStore: {}, sameDayPickUp: {} };
    }
);

export default initDeliveryOptions;
